import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMap } from '../../features/mapping/MapContext';
import { useApi } from '../../api/api';
import './PanelLayout.css';
import ErrorBoundary from '../../features/mapping/ErrorBoundary/ErrorBoundary';
import TopPanel from '../TopPanel/TopPanel';
import ColumnPanel from '../ColumnPanel/ColumnPanel'
import BottomPanel from '../BottomPanel/BottomPanel';
import MoreEditOptionsModal from '../MoreEditOptionsModal/MoreEditOptionsModal';

const PanelLayout = ({
  leftPanelTitle,
  leftPanelLayout,
  rightPanelTitle,
  rightPanelLayout,
  leftPanelInjections,
  rightPanelInjections,
  topPanelModules,
  bottomPanelInitialLayers,
  bottomPanelVisible = true,
  editMode = false,
  pageType = null,
  resourceId = null,
  onSpeciesLEKLoad,
  onToggleSpeciesLEKData,
  speciesLEKData = null,
  onModuleSelect,
  selectedModuleId,
  stockImageUrl,
}) => {
  const { layerController } = useMap();
  const { getPageComponentSlugs } = useApi();
  const prevInitialLayersRef = useRef(null);
  const [moreEditOptionsOpen, setMoreEditOptionsOpen] = useState(false);
  const [activeStepComponentSlugs, setActiveStepComponentSlugs] = useState([]);

  // Fetch component slugs for selected top panel step (for left panel highlighting)
  useEffect(() => {
    if (!selectedModuleId || !getPageComponentSlugs) {
      setActiveStepComponentSlugs([]);
      return;
    }
    let cancelled = false;
    getPageComponentSlugs(selectedModuleId)
      .then((response) => {
        if (!cancelled) {
          const slugs = response?.data ?? [];
          setActiveStepComponentSlugs(Array.isArray(slugs) ? slugs : []);
        }
      })
      .catch(() => {
        if (!cancelled) setActiveStepComponentSlugs([]);
      });
    return () => { cancelled = true; };
  }, [selectedModuleId, getPageComponentSlugs]);

  // Sync bottomPanelInitialLayers to layerController when it changes (e.g. page navigation).
  // On full-page refresh (Plan/Region), the map may not be ready when layout arrives—listen for mapControllerReady.
  useEffect(() => {
    if (!layerController) return;
    const initialLayers = bottomPanelInitialLayers ?? {};
    let cancelled = false;

    const applyLayers = async () => {
      if (cancelled) return;
      const initPromise = layerController.initializationPromise ?? layerController.getInitializationPromise?.();
      if (initPromise) await initPromise;
      if (cancelled) return;
      if (!layerController.isReady()) return;
      const prevInitialLayers = prevInitialLayersRef.current || {};
      const changed = JSON.stringify(prevInitialLayers) !== JSON.stringify(initialLayers);
      // Always apply when initialLayers is empty (Home, or pages with no data layers)—clears stale layers from previous page
      const hasNoLayers = Object.keys(initialLayers).length === 0;
      if (changed || hasNoLayers) {
        prevInitialLayersRef.current = initialLayers;
        await layerController.clearAllLayersAndFilters(initialLayers);
        await layerController.initializeLayers(initialLayers);
      }
    };

    const onReady = () => applyLayers();

    if (layerController.isReady()) {
      applyLayers().catch((err) => {
        if (!cancelled) console.error('Failed to sync initial layers:', err);
      });
    } else {
      window.addEventListener('mapControllerReady', onReady);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('mapControllerReady', onReady);
    };
  }, [layerController, bottomPanelInitialLayers]);

  // Add/remove no-scroll class to body when component mounts/unmounts
  useEffect(() => {
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('no-scroll');

    return () => {
      document.body.classList.remove('no-scroll');
      document.documentElement.classList.remove('no-scroll');
    };
  }, []);

  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <div className="loading-state">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <div className="loading-state">Please log in to view the Ocean Dashboard</div>;
  }

  return (
    <div className={`noscroll-dashboard no-scroll-page ${editMode ? 'edit-mode-active' : ''}`}>
      {editMode && (
        <div className="edit-mode-banner">
          <span>Layout edit mode — <a href={window.location.pathname}>Exit edit mode</a></span>
          <button
            type="button"
            className="edit-mode-banner-more-btn"
            onClick={() => setMoreEditOptionsOpen(true)}
          >
            More Edit Options
          </button>
        </div>
      )}
      {editMode && (
        <MoreEditOptionsModal
          isOpen={moreEditOptionsOpen}
          onClose={() => setMoreEditOptionsOpen(false)}
          pageType={pageType}
          resourceId={resourceId}
        />
      )}
      <div className="noscroll-dashboard-layout">
          <div className="noscroll-left-column">
            <ColumnPanel title={leftPanelTitle} layout={leftPanelLayout} injections={leftPanelInjections} editMode={editMode} panelType="left" pageType={pageType} resourceId={resourceId} highlightedComponentSlugs={activeStepComponentSlugs}/>
          </div>

          <div className="noscroll-center-column">
            {topPanelModules && (
              <TopPanel
                modules={topPanelModules}
                onModuleSelect={onModuleSelect}
                selectedModuleId={selectedModuleId}
                stockImageUrl={stockImageUrl}
                editMode={editMode}
              />
            )}
            {/* Map window - transparent panel showing PersistentCesiumBackground; clicks handled by MapController */}
            <ErrorBoundary>
              <div className="noscroll-lek-panel">
                <div className="noscroll-lek-content">
                  <div className="map-container" />
                </div>
              </div>
            </ErrorBoundary>

            {bottomPanelVisible && (
              <BottomPanel
                initialLayers={bottomPanelInitialLayers ?? {}}
                visible={bottomPanelVisible}
                editMode={editMode}
                onSpeciesLEKLoad={onSpeciesLEKLoad}
                onToggleSpeciesLEKData={onToggleSpeciesLEKData}
                speciesLEKData={speciesLEKData}
                pageType={pageType}
                resourceId={resourceId}
              />
            )}
          </div>

          <div className="noscroll-right-column">
            <ColumnPanel title={rightPanelTitle} layout={rightPanelLayout} injections={rightPanelInjections} editMode={editMode} panelType="right" pageType={pageType} resourceId={resourceId}/>
          </div>
        </div>
      </div>
  );
};

export default PanelLayout;
